import * as P from "../program-state"
import * as T from "../resource/types"
import R from "react"
import {Emit_main} from "../navigation"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faArrowUpRightFromSquare, faBan, faFloppyDisk, faPencil, faSync, faTrash} from "@fortawesome/free-solid-svg-icons"
import {useParams} from "react-router-dom"
import {z} from "zod"

import {Eres_card, Eres_checkbox,Eres_collapse,Eres_header2xl, Eres_headerxl,
    Eres_iconbutton, Eres_spin_placeholder, stop_propagation}
    from "../controls"

import {user_state, issue_json_get, program_state, run_once_on_component_creation,
    use_authenticated_program_state, issue_json_post, unreachable, result,
    http_error, issue_json_delete, Err, Ok, v1api, limited_concurrency,
    filter_map, set_difference, authn_token} from "../program-state"

import { Asset_instruction_placeholder, Facebook_business_center_instruction } from "./network-instructions"

// Note that the eres database ID for a foreign asset can change over time
// as assets are created and deleted from the database. Secure enough hashes,
// using something like sha256 or blake3, could be used to derive a stable
// unique identifier for any foreign ID, but then we'd have to deal with
// potentially-contiguous integer foreign IDs being mapped to points with
// arbitrary distance between them in 256-bit space, which isn't great for
// b-tree index locality. So just "index" things in the frontend using
// prefix-tagged facebook IDs; these tagged fbids will be mapped back to
// ephemeral eres database IDs only when communicating with our current API.
type has_id = { id : number }
type has_name = { name : string }
type has_tagged_id<id_t> = { tagged_id : id_t }
type has_username = { username : string }
type network_dbid = string & { readonly __tag : unique symbol }
type tagged_network_id = ["fb" | "li" | "tt" | "x", network_dbid]

const editable : unique symbol = Symbol.for("editable")
const not_editable : unique symbol = Symbol.for("not_editable")

type fields_are_arrays = { [field_name : string] : any[] }
type data_not_loaded_yet = { readonly tag : "loading" }
type network_loaded<assets_t extends fields_are_arrays> = { readonly tag : "cna-loaded", readonly currently_associated : assets_t }
type network_preparing_edit<assets_t extends fields_are_arrays> = { readonly tag : "cna-preparing-edit", readonly currently_associated : assets_t }
type network_editing<assets_t extends fields_are_arrays, id_t> = {
    readonly tag : "cna-editing",
    readonly already_used_ids : Set<id_t>,
    readonly selected_ids : Set<id_t>,
    readonly id_to_eresid : Map<id_t, number>,
    readonly currently_associated : assets_t,
    readonly not_currently_associated : assets_t,
}
type network_edits_submitted<assets_t extends fields_are_arrays, id_t> = {
    readonly tag : "cna-edits-submitted",
    readonly already_used_ids : Set<id_t>,
    readonly selected_ids : Set<id_t>,
    readonly currently_associated : assets_t,
    readonly not_currently_associated : assets_t,
}
export type pending_edit_state<assets_t extends fields_are_arrays, id_t> = data_not_loaded_yet | edit_state<assets_t, id_t>
type edit_state<assets_t extends fields_are_arrays, id_t> =
    | network_loaded<assets_t>
    | network_preparing_edit<assets_t>
    | network_editing<assets_t, id_t>
    | network_edits_submitted<assets_t, id_t>
type fb_edit_state = edit_state<facebook_assets, tagged_fbid>
type li_edit_state = edit_state<linkedin_assets, tagged_liid>
type tt_edit_state = edit_state<Omit<tiktok_assets, "tt_users">, tagged_ttid>

export const Tablecol = ({children, align} : R.PropsWithChildren<{align? : "right"}>) => {
    return <td className={`tw-whitespace-nowrap tw-px-1.5 first:tw-ps-9 last:tw-pe-6 tw-py-1.5 tw-text-sm ${align === "right" ? "tw-text-right" : ""}`}>{children}</td>
}
const parse_any = z.any()
const get_name = (x : has_name) => x.name
const get_username = (x : has_username) => x.username
const sort_by_name = (x : has_name, y : has_name) => x.name.localeCompare(y.name)
const sort_by_username = (x : P.facebook_instagram_parse_t, y : P.facebook_instagram_parse_t) => x.username.localeCompare(y.username)
const _tag_id = <tag_t, id_t, ret_t>(tag : tag_t, id : id_t) : ret_t => `${tag}${id}` as ret_t

type asset_layout<t> = [string, string, keyof t, (r : any) => string, (r : any) => string, (r : any) => string, typeof editable | typeof not_editable, R.FC]
type network_stuff<assets_t extends fields_are_arrays, id_t> = {
    begin_edit : (token : authn_token, dbid : string, start_state : network_loaded<assets_t>, set_eph_state : (e : edit_state<assets_t, id_t>) => void) => Promise<void>,
    fetch_latest : (token : authn_token, dbid : string, set_eph_state : (e : edit_state<assets_t, id_t>) => void) => Promise<void>,
    submit_edited_data : (token : authn_token, dbid : string, start_state : network_editing<assets_t, id_t>, new_selected_id_set : Set<any>, set_eph_state : (e : edit_state<assets_t, id_t>) => void) => Promise<void>,
    validate_tid : (maybe_tid : string) => id_t | undefined,
    asset_table_layout : asset_layout<assets_t>[],
    delete_asset? : (token : authn_token, dbid : string, field_name : keyof assets_t, eres_id : number, set_eph_state : (e : edit_state<assets_t, id_t>) => void) => Promise<void>,
}

/******************************************************************************
 ** Facebook remote data send/receive [
 ******************************************************************************/

type has_facebook_id = { facebook_id : P.fbid }
type fb_asset_tag = "pa-" | "aa-" | "bm-" | "ia-" | "pi-"
type tagged_fbid = string & { readonly __tag : unique symbol };

type facebook_preparing_edit = network_preparing_edit<facebook_assets>
type facebook_editing = network_editing<facebook_assets, tagged_fbid>
type has_tagged_fbid = has_tagged_id<tagged_fbid>
type wrap_fields<t, taggedid> = { [k in keyof t] : (t[k] & taggedid)[] }
type facebook_assets = wrap_fields<P.facebook__asset_types_record, has_tagged_fbid>

const prefix_fbidtag = <t2, t extends t2>(xs : (has_facebook_id & t)[], sortfunc : (x : t2 , y : t2) => number, prefix : fb_asset_tag) : (t & has_tagged_fbid)[] => {
    xs.sort(sortfunc)
    xs.forEach((val) => {
        const v = val as unknown as t & has_tagged_fbid
        v.tagged_id = facebook__tag_id(prefix, val.facebook_id)
    })
    return xs as unknown as (t & has_tagged_fbid)[]
}


const facebook__tag_id = (tag : fb_asset_tag, fbid : P.fbid) : tagged_fbid => _tag_id(tag, fbid)
const facebook__validate_tid = (maybe_fbid : string) : tagged_fbid | undefined => {
    const tag = maybe_fbid.substring(0, 3)
    switch (tag) {
    case "pa-": case "aa-": case "bm-": case "ia-": case "pi-": return maybe_fbid as tagged_fbid
    }
    return undefined
}

const sync_facebook_page_parse = z.object({
    id : z.number(),
    facebook_id : P.z_fbid(),
    name : z.string(),
})
const wrapped_sync_facebook_pages_parse = z.object({
    status : z.string(),
    pages : z.array(sync_facebook_page_parse),
})

const sync_facebook_ad_account_parse = z.object({
    id : z.number(),
    facebook_id : P.z_fbid(),
    name : z.string(),
})
const wrapped_sync_facebook_ad_accounts_parse = z.object({
    status : z.string(),
    personal_ad_accounts : z.array(sync_facebook_ad_account_parse),
})

const sync_instagram_profile_parse = z.object({
    id : z.number(),
    facebook_id : P.z_fbid(),
    username : z.string(),
})
const wrapped_sync_instagram_profiles_parse = z.object({
    status : z.string(),
    instagrams : z.array(sync_instagram_profile_parse),
})

const sync_pixels_parse = z.object({
    id : z.number(),
    facebook_id : P.z_fbid(),
    name : z.string(),
})
const wrapped_sync_pixels_parse = z.object({
    status : z.string(),
    pixels : z.array(sync_pixels_parse),
})

const facebook__fetch_latest = async (token : authn_token, dbid : string, set_eph_state : (e : fb_edit_state) => void) => {
    const reqs = P.facebook__requests_for_all_assets(dbid, token)
    const r = await P.all_concurrently_rec(reqs)
    const {fb_page : p, fb_adaccount : a, fb_businessmanager : b, fb_instagram : i, fb_pixel : pix} = r
    const [pok, presp] = p
    const [aok, aresp] = a
    const [bok, bresp] = b
    const [iok, iresp] = i
    const [pxok, pxresp] = pix

    // @ts-ignore
    // await window._eres_program_state.program_state.ps_sleep(2000)
    if (pok && aok && bok && iok && pxok) {
        set_eph_state({
            tag : "cna-loaded",
            currently_associated : {
                fb_page : prefix_fbidtag(presp, sort_by_name, "pa-"),
                fb_adaccount : prefix_fbidtag(aresp, sort_by_name, "aa-"),
                fb_businessmanager : prefix_fbidtag(bresp, sort_by_name, "bm-"),
                fb_instagram : prefix_fbidtag(iresp, sort_by_username, "ia-"),
                fb_pixel : prefix_fbidtag(pxresp, sort_by_name, "pi-") }})
    } else {
        console.log("XXXih: do error handling", r)
    }
}

const facebook__begin_edit = async (token : authn_token, dbid : string, start_state : network_loaded<facebook_assets>, set_eph_state : (e : fb_edit_state) => void) => {
    {
        const next_state : facebook_preparing_edit = { tag : "cna-preparing-edit", currently_associated : start_state.currently_associated }
        set_eph_state(next_state)
    }
    const pages_task = issue_json_post(v1api`social/facebook/sync-facebook-pages/${dbid}`, undefined, wrapped_sync_facebook_pages_parse, token)
    const adact_task = issue_json_post(v1api`social/facebook/sync-ad-accounts/${dbid}`, undefined, wrapped_sync_facebook_ad_accounts_parse, token)
    const igact_task = issue_json_get(v1api`social/facebook/${dbid}/sync/instagrams`, wrapped_sync_instagram_profiles_parse, token)
    const pixel_task = issue_json_get(v1api`social/facebook/${dbid}/sync/pixels`, wrapped_sync_pixels_parse, token)
    const [p, a, i, px] = await Promise.all([pages_task, adact_task, igact_task, pixel_task])
    const [pok, presp] = p
    const [aok, aresp] = a
    const [iok, iresp] = i
    const [pxok, pxresp] = px
    if (pok && aok && iok && pxok) {
        const currently_used_fbids = new Set<tagged_fbid>()
        const fbid_to_eresid = new Map<tagged_fbid, number>()
        const add_eresid = (xs : (has_tagged_fbid & has_id)[]) => {
            for (const e of xs) fbid_to_eresid.set(e.tagged_id, e.id)
        }
        const add_current = (xs : (has_tagged_fbid & has_id)[]) => {
            for (const e of xs) currently_used_fbids.add(e.tagged_id)
            add_eresid(xs)
        }
        // Facebook IDs should be associated with eres database IDs in exactly
        // this order: currently-active fbids, followed by "indexed" but
        // inactive ids. The reason for this is that if a user unchecks an
        // fbid, then we'll want to send a series of DELETE requests for the
        // old eres IDs; in contrast, if the user checks an unused fbid, then
        // we'll want to send one or more POST requests containing the "index"
        // eres IDs.
        add_current(start_state.currently_associated.fb_page)
        add_current(start_state.currently_associated.fb_adaccount)
        add_current(start_state.currently_associated.fb_businessmanager)
        add_current(start_state.currently_associated.fb_instagram)
        add_current(start_state.currently_associated.fb_pixel)
        const filter_prefix_fbtag = (prefix : fb_asset_tag) => <t,>(x : t & has_facebook_id) : result<t & has_tagged_fbid, undefined> => {
            const tagged_id = facebook__tag_id(prefix, x.facebook_id)
            if (currently_used_fbids.has(tagged_id)) return [Err, undefined]
            // This changes the data in-place, but there's minimal risk because nothing
            // else holds a reference to the arrays being transformed.
            const r = x as unknown as t & has_tagged_fbid
            r.tagged_id = tagged_id
            return [Ok, r]
        }
        const not_currently_associated : facebook_assets = {
            fb_page : filter_map(presp.pages, filter_prefix_fbtag("pa-")),
            fb_adaccount : filter_map(aresp.personal_ad_accounts, filter_prefix_fbtag("aa-")),
            fb_businessmanager : [],
            fb_instagram : filter_map(iresp.instagrams, filter_prefix_fbtag("ia-")),
            fb_pixel : filter_map(pxresp.pixels, filter_prefix_fbtag("pi-")),
        }
        add_eresid(not_currently_associated.fb_page)
        add_eresid(not_currently_associated.fb_adaccount)
        add_eresid(not_currently_associated.fb_businessmanager)
        add_eresid(not_currently_associated.fb_instagram)
        add_eresid(not_currently_associated.fb_pixel)
        set_eph_state({
            tag : "cna-editing",
            already_used_ids : currently_used_fbids,
            selected_ids : currently_used_fbids,
            id_to_eresid : fbid_to_eresid,
            currently_associated : start_state.currently_associated,
            not_currently_associated : not_currently_associated,
        })
    } else {
        // XXXih: TODO: display an error message
        set_eph_state(start_state)
        console.log("begin edit failed!!!")
    }
}

const facebook__submit_edited_data = async (token : authn_token, dbid : string, start_state : facebook_editing, new_selected_id_set : Set<tagged_fbid>, set_eph_state : (e : fb_edit_state) => void) => {
    set_eph_state({
        tag : "cna-edits-submitted",
        already_used_ids : start_state.already_used_ids,
        selected_ids : new_selected_id_set,
        currently_associated : start_state.currently_associated,
        not_currently_associated : start_state.not_currently_associated,
    })
    const id_sets : Record<fb_asset_tag, { add : tagged_fbid[], remove : tagged_fbid[] }> = {
        "pa-": { add : [], remove : [] },
        "aa-": { add : [], remove : [] },
        "bm-": { add : [], remove : [] },
        "ia-": { add : [], remove : [] },
        "pi-": { add : [], remove : [] },
    }
    const filter_by_prefix = (xs : Set<tagged_fbid>, prefix : fb_asset_tag) : tagged_fbid[] => {
        const ret : tagged_fbid[] = []
        xs.forEach((val : tagged_fbid) => {
            if (val.startsWith(prefix)) {
                ret.push(val)
            }
        })
        return ret
    }
    const ids_to_add = set_difference(new_selected_id_set, start_state.already_used_ids)
    const ids_to_remove = set_difference(start_state.already_used_ids, new_selected_id_set)
    console.log(ids_to_add)
    console.log(ids_to_remove)
    const extract_category = (prefix : fb_asset_tag) : void => {
        const add_ = filter_by_prefix(ids_to_add, prefix)
        const remove_ = filter_by_prefix(ids_to_remove, prefix)
        const dst = id_sets[prefix]
        dst.add = add_
        dst.remove = remove_
    }
    const _tid_to_eresid = (tagged_id : tagged_fbid, errctx : string) : number | undefined => {
        const eresid = start_state.id_to_eresid.get(tagged_id)
        if (eresid === undefined) console.log(`${errctx}: missing fbid->eresid`)
        return eresid
    }
    const __tid_to_eresid = (errctx : string) => (tagged_id : tagged_fbid) : result<number, undefined> => {
        const v = _tid_to_eresid(tagged_id, errctx)
        if (v === undefined) return [Err, undefined]
        return [Ok, v]
    }
    extract_category("pa-")
    extract_category("aa-")
    extract_category("bm-")
    extract_category("ia-")
    extract_category("pi-")
    const tasks : (() => Promise<result<any, TypeError | http_error | SyntaxError | z.ZodError>>)[] = []

    const pages_body = { ids_to_sync : filter_map(id_sets["pa-"].add, __tid_to_eresid("fbpage add")) }
    if (pages_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/facebook/sync-selected-pages/${dbid}`, pages_body, parse_any, token))
    }
    for (const tid of id_sets["pa-"].remove) {
        const eresid = _tid_to_eresid(tid, "fbpage remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/facebook/pages/${eresid}`, parse_any, token))
        }
    }

    const adact_body = { to_sync_ad_accounts : filter_map(id_sets["aa-"].add, __tid_to_eresid("fbadacct add")) }
    if (adact_body.to_sync_ad_accounts.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/facebook/sync-selected-ad-accounts/${dbid}`, adact_body, parse_any, token))
    }
    for (const tid of id_sets["aa-"].remove) {
        const eresid = _tid_to_eresid(tid, "fbadacct remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/facebook/${dbid}/ad-accounts/${eresid}`, parse_any, token))
        }
    }

    const ig_body = { ids_to_sync : filter_map(id_sets["ia-"].add, __tid_to_eresid("fb ig acct add")) }
    if (ig_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/facebook/sync-selected-instagram-assets/${dbid}`, ig_body, parse_any, token))
    }
    for (const tid of id_sets["ia-"].remove) {
        const eresid = _tid_to_eresid(tid, "fb ig acct remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/facebook/${dbid}/instagrams/${eresid}`, parse_any, token))
        }
    }

    const pixel_body = { ids_to_sync : filter_map(id_sets["pi-"].add, __tid_to_eresid("fbpixel add")) }
    if (pixel_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/facebook/sync-selected-pixels/${dbid}`, pixel_body, parse_any, token))
    }
    for (const tid of id_sets["pi-"].remove) {
        const eresid = _tid_to_eresid(tid, "fbpixel remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/facebook/${dbid}/pixels/${eresid}`, parse_any, token))
        }
    }

    const resp = await limited_concurrency(5, tasks)
    for (const [ok, val] of resp) {
        if (!ok) {
            console.log(`XXXih: TODO: got error: ${val}`)
        }
    }
    console.log(resp)
    await facebook__fetch_latest(token, dbid, set_eph_state)
}
const fb_page_url = (x : has_facebook_id) => `https://facebook.com/${x.facebook_id}`
const fb_adaccount_url = (x : has_facebook_id) => `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${x.facebook_id}`
const fb_bm_url = (x : has_facebook_id) => `https://business.facebook.com/latest/home?business_id=${x.facebook_id}`
const fb_ig_url = (x : has_username) => `https://instagram.com/${x.username}`
const fb_px_url = (x : has_facebook_id) => `https://www.facebook.com/events_manager2/list/pixel/${x.facebook_id}`
const get_raw_fbid = (x : has_facebook_id) => x.facebook_id

export const facebook__netstuff : network_stuff<facebook_assets, tagged_fbid> = {
    begin_edit : facebook__begin_edit,
    fetch_latest : facebook__fetch_latest,
    submit_edited_data : facebook__submit_edited_data,
    validate_tid : facebook__validate_tid,
    asset_table_layout : [
        ["Pages",              "pages",              "fb_page",            get_name,     get_raw_fbid, fb_page_url,      editable, Asset_instruction_placeholder],
        ["Ad Accounts",        "ad accounts",        "fb_adaccount",       get_name,     get_raw_fbid, fb_adaccount_url, editable, Asset_instruction_placeholder],
        ["Business Managers",  "business managers",  "fb_businessmanager", get_name,     get_raw_fbid, fb_bm_url,        not_editable, Facebook_business_center_instruction],
        ["Instagram Accounts", "instagram accounts", "fb_instagram",       get_username, get_raw_fbid, fb_ig_url,        editable, Asset_instruction_placeholder],
        ["Pixels",             "pixels",             "fb_pixel",           get_name,     get_raw_fbid, fb_px_url,        editable, Asset_instruction_placeholder],
    ],
}

/******************************************************************************
 ** Facebook remote data send/receive ]
 ******************************************************************************/

/******************************************************************************
 ** Linkedin remote data send/receive [
 ******************************************************************************/

type li_asset_tag = "li-aa-" | "li-or-" | "li-it-"
type tagged_liid = string & { readonly __tag : unique symbol }
type has_tagged_liid = { tagged_id : tagged_liid }
type has_linkedin_id = { linkedin_id : P.liid }

const linkedin__tag_id = (tag : li_asset_tag, liid : P.liid) : tagged_liid => _tag_id(tag, liid)
const linkedin__validate_tid = (maybe_tid : string) : tagged_liid | undefined => {
    const tag = maybe_tid.substring(0, 6)
    switch (tag) {
    case "li-aa-": case "li-or-": case "li-it-": return maybe_tid as tagged_liid
    }
    return undefined
}

type linkedin_assets = {
    ad_accounts : (P.linkedin__ad_account_t & has_tagged_liid)[],
    organizations : (P.linkedin__organization_t & has_tagged_liid)[],
    insight_tags : (P.linkedin__insight_tag_t & has_tagged_liid)[],
}

const linkedin__sync_ad_accounts_parse = z.object({
    id : z.number(),
    linkedin_id : P.z_liid(),
    name : z.string(),
})
const linkedin__wrapped_sync_ad_accounts_parse = z.object({
    status : z.string(),
    ad_accounts : z.array(linkedin__sync_ad_accounts_parse),
})

const linkedin__sync_organizations_parse = z.object({
    id : z.number(),
    linkedin_id : P.z_liid(),
    name : z.string().nullish(),
})
const linkedin__wrapped_sync_organizations_parse = z.object({
    status : z.string(),
    organizations : z.array(linkedin__sync_organizations_parse),
})

const linkedin__wrapped_sync_insight_tags_parse = z.object({
    status : z.string(),
    insight_tags : P.linkedin__insight_tags_parse,
})

const linkedin__prefix_liidtag = <t2, t extends t2>(xs : (has_linkedin_id & t)[], sortfunc : (x : t2 , y : t2) => number, prefix : li_asset_tag) : (t & has_tagged_liid)[] => {
    xs.sort(sortfunc)
    xs.forEach((val) => {
        const v = val as unknown as t & has_tagged_liid
        v.tagged_id = linkedin__tag_id(prefix, val.linkedin_id)
    })
    return xs as unknown as (t & has_tagged_liid)[]
}

type linkedin__has_id_and_optional_name = { name? : string | undefined | null } & has_linkedin_id
const sort_by_name_or_liid = (x : linkedin__has_id_and_optional_name, y : linkedin__has_id_and_optional_name) => {
    if (x.name && y.name) return x.name.localeCompare(y.name)
    if (x.name) return 1
    if (y.name) return -1
    if (x.linkedin_id < y.linkedin_id) return -1
    if (x.linkedin_id > y.linkedin_id) return 1
    return 0
}

const sort_by_liid = (x : has_linkedin_id, y : has_linkedin_id) => {
    if (x.linkedin_id < y.linkedin_id) return -1
    if (x.linkedin_id > y.linkedin_id) return 1
    return 0
}

const linkedin__fetch_latest = async (token : authn_token, dbid : string, set_eph_state : (e : li_edit_state) => void) => {
    const reqs = P.linkedin__requests_for_all_assets(dbid, token)
    const {li_adaccount : a, li_organization : o, li_insighttag : i} = await P.all_concurrently_rec(reqs)
    const [aok, aresp] = a
    const [ook, oresp] = o
    const [iok, iresp] = i
    if (aok && ook && iok) {
        set_eph_state({
            tag : "cna-loaded",
            currently_associated : {
                ad_accounts : linkedin__prefix_liidtag(aresp, sort_by_name, "li-aa-"),
                organizations : linkedin__prefix_liidtag(oresp, sort_by_name_or_liid, "li-or-"),
                insight_tags : linkedin__prefix_liidtag(iresp, sort_by_liid, "li-it-"),
            },
        })
    } else {
        console.log("failed!!!", aresp, oresp, iresp)
    }
}

const linkedin__begin_edit = async (token : authn_token, dbid : string, start_state : network_loaded<linkedin_assets>, set_eph_state : (e : li_edit_state) => void) => {
    {
        const next_state : network_preparing_edit<linkedin_assets> = { tag : "cna-preparing-edit", currently_associated : start_state.currently_associated }
        set_eph_state(next_state)
    }
    const adact_task = issue_json_post(v1api`social/linkedin/sync-ad-accounts/${dbid}/`, undefined, linkedin__wrapped_sync_ad_accounts_parse, token)
    const org_task = issue_json_post(v1api`social/linkedin/sync-organizations/${dbid}/`, undefined, linkedin__wrapped_sync_organizations_parse, token)
    const itag_task = issue_json_post(v1api`social/linkedin/sync-insight-tags/${dbid}/`, undefined, linkedin__wrapped_sync_insight_tags_parse, token)
    const result = await Promise.all([adact_task, org_task, itag_task])
    const [a, o, i] = result
    const [a_ok, aresp] = a
    const [o_ok, oresp] = o
    const [i_ok, iresp] = i
    console.log(result)
    if (a_ok && o_ok && i_ok) {
        const currently_used_liids = new Set<tagged_liid>()
        const liid_to_eresid = new Map<tagged_liid, number>()
        const add_eresid = (xs : (has_tagged_liid & has_id)[]) => {
            for (const e of xs) liid_to_eresid.set(e.tagged_id, e.id)
        }
        const add_current = (xs : (has_tagged_liid & has_id)[]) => {
            for (const e of xs) currently_used_liids.add(e.tagged_id)
            add_eresid(xs)
        }
        add_current(start_state.currently_associated.ad_accounts)
        add_current(start_state.currently_associated.organizations)
        add_current(start_state.currently_associated.insight_tags)
        const filter_prefix_litag = (prefix : li_asset_tag) => <t,>(x : t & has_linkedin_id) : result<t & has_tagged_liid, undefined> => {
            const tagged_id = linkedin__tag_id(prefix, x.linkedin_id)
            if (currently_used_liids.has(tagged_id)) return [Err, undefined]
            const r = x as unknown as t & has_tagged_liid
            r.tagged_id = tagged_id
            return [Ok, r]
        }
        const not_currently_associated : linkedin_assets = {
            ad_accounts : filter_map(aresp.ad_accounts, filter_prefix_litag("li-aa-")),
            organizations : filter_map(oresp.organizations, filter_prefix_litag("li-or-")),
            insight_tags : filter_map(iresp.insight_tags, filter_prefix_litag("li-it-")),
        }
        add_eresid(not_currently_associated.ad_accounts)
        add_eresid(not_currently_associated.organizations)
        add_eresid(not_currently_associated.insight_tags)
        set_eph_state({
            tag : "cna-editing",
            already_used_ids : currently_used_liids,
            selected_ids : currently_used_liids,
            id_to_eresid : liid_to_eresid,
            currently_associated : start_state.currently_associated,
            not_currently_associated : not_currently_associated,
        })
    } else {
        // XXXih: TODO: display an error message
        set_eph_state(start_state)
        console.log("begin edit failed!!!")
    }
}

const linkedin__submit_edited_data = async (token : authn_token, dbid : string, start_state : network_editing<linkedin_assets, tagged_liid>, new_selected_id_set : Set<tagged_liid>, set_eph_state : (e : li_edit_state) => void) => {
    set_eph_state({
        tag : "cna-edits-submitted",
        already_used_ids : start_state.already_used_ids,
        selected_ids : new_selected_id_set,
        currently_associated : start_state.currently_associated,
        not_currently_associated : start_state.not_currently_associated,
    })
    const id_sets : Record<li_asset_tag, { add : tagged_liid[], remove : tagged_liid[] }> = {
        "li-aa-": { add : [], remove : [] },
        "li-or-": { add : [], remove : [] },
        "li-it-": { add : [], remove : [] },
    }
    const filter_by_prefix = (xs : Set<tagged_liid>, prefix : li_asset_tag) : tagged_liid[] => {
        const ret : tagged_liid[] = []
        xs.forEach((val : tagged_liid) => {
            if (val.startsWith(prefix)) {
                ret.push(val)
            }
        })
        return ret
    }
    const ids_to_add = set_difference(new_selected_id_set, start_state.already_used_ids)
    const ids_to_remove = set_difference(start_state.already_used_ids, new_selected_id_set)
    console.log(new_selected_id_set)
    console.log(ids_to_add)
    console.log(ids_to_remove)
    const extract_category = (prefix : li_asset_tag) : void => {
        const add_ = filter_by_prefix(ids_to_add, prefix)
        const remove_ = filter_by_prefix(ids_to_remove, prefix)
        const dst = id_sets[prefix]
        dst.add = add_
        dst.remove = remove_
    }
    const _tid_to_eresid = (tagged_id : tagged_liid, errctx : string) : number | undefined => {
        const eresid = start_state.id_to_eresid.get(tagged_id)
        if (eresid === undefined) console.log(`${errctx}: missing fbid->eresid`)
        return eresid
    }
    const __tid_to_eresid = (errctx : string) => (tagged_id : tagged_liid) : result<number, undefined> => {
        const v = _tid_to_eresid(tagged_id, errctx)
        if (v === undefined) return [Err, undefined]
        return [Ok, v]
    }
    extract_category("li-aa-")
    extract_category("li-or-")
    extract_category("li-it-")
    const tasks : (() => Promise<result<any, TypeError | http_error | SyntaxError | z.ZodError>>)[] = []

    const adact_body = { ids_to_sync : filter_map(id_sets["li-aa-"].add, __tid_to_eresid("liadacct add")) }
    if (adact_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/linkedin/sync-selected-ad-accounts/${dbid}`, adact_body, parse_any, token))
    }
    for (const tid of id_sets["li-aa-"].remove) {
        const eresid = _tid_to_eresid(tid, "liadacct remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/linkedin/${dbid}/ad-accounts/${eresid}`, parse_any, token))
        }
    }

    const org_body = { ids_to_sync : filter_map(id_sets["li-or-"].add, __tid_to_eresid("liorg add")) }
    if (org_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/linkedin/sync-selected-organizations/${dbid}`, org_body, parse_any, token))
    }
    for (const tid of id_sets["li-or-"].remove) {
        const eresid = _tid_to_eresid(tid, "liorg remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/linkedin/${dbid}/organizations/${eresid}`, parse_any, token))
        }
    }

    const itag_body = { ids_to_sync : filter_map(id_sets["li-it-"].add, __tid_to_eresid("li insight tags add")) }
    if (org_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/linkedin/sync-selected-insight-tags/${dbid}`, itag_body, parse_any, token))
    }
    for (const tid of id_sets["li-it-"].remove) {
        const eresid = _tid_to_eresid(tid, "li insight tags remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/linkedin/${dbid}/insight-tags/${eresid}`, parse_any, token))
        }
    }

    const resp = await limited_concurrency(5, tasks)
    for (const [ok, val] of resp) {
        if (!ok) {
            console.log(`XXXih: TODO: got error: ${val}`)
        }
    }
    console.log(resp)
    await linkedin__fetch_latest(token, dbid, set_eph_state)
}

const get_raw_liid = (x : has_linkedin_id) => x.linkedin_id.toString()
const XXXih_fake_url = (_ : any) => ""
const get_name_or_create_from_liid = (x : linkedin__has_id_and_optional_name) => {
    if (x.name) return x.name
    return `[no name; LinkedIn ID = ${x.linkedin_id}]`
}
const name_from_liid = (x : has_linkedin_id) => `Insight tag ID ${x.linkedin_id}`
const linkedin__ad_account_url = (x : has_linkedin_id) => `https://www.linkedin.com/campaignmanager/accounts/${x.linkedin_id}/`
const linkedin__organization_url = (x : has_linkedin_id) => `https://www.linkedin.com/company/${x.linkedin_id}/admin/`
// insight tag: https://www.linkedin.com/campaignmanager/accounts/509306360/insight-tag

export const linkedin__netstuff : network_stuff<linkedin_assets, tagged_liid> = {
    begin_edit : linkedin__begin_edit,
    fetch_latest : linkedin__fetch_latest,
    submit_edited_data : linkedin__submit_edited_data,
    validate_tid : linkedin__validate_tid,
    asset_table_layout : [
        ["Ad Accounts",   "ad accounts",   "ad_accounts",   get_name,                     get_raw_liid, linkedin__ad_account_url,   editable, Asset_instruction_placeholder],
        ["Organizations", "organizations", "organizations", get_name_or_create_from_liid, get_raw_liid, linkedin__organization_url, editable, Asset_instruction_placeholder],
        ["Insight Tags",  "insight tags",  "insight_tags",  name_from_liid,               get_raw_liid, XXXih_fake_url,             editable, Asset_instruction_placeholder],
    ],
}

/******************************************************************************
 ** Linkedin remote data send/receive ]
 ******************************************************************************/

/******************************************************************************
 ** Tiktok remote data send/receive [
 ******************************************************************************/

type tt_asset_tag = "tt-aa-" | "tt-bc-" | "tt-id-" | "tt-px-"
type tagged_ttid = string & { readonly __tag : unique symbol }
type has_tagged_ttid = { tagged_id : tagged_ttid }

const tiktok__tag_id = (tag : tt_asset_tag, ttid : P.ttid) : tagged_ttid => _tag_id(tag, ttid)
const tiktok__validate_tid = (maybe_tid : string) : tagged_ttid | undefined => {
    const tag = maybe_tid.substring(0, 6)
    switch (tag) {
    case "tt-aa-": case "tt-bc-": case "tt-id-": case "tt-px-": return maybe_tid as tagged_ttid
    }
    return undefined
}

type tiktok_assets = wrap_fields<Omit<P.tiktok_assets_record, "tt_users">, has_tagged_ttid>
const tiktok__wrapped_sync_ad_accounts_parse = z.object({
    message : z.string(),
    data : P.tiktok__ad_accounts_parse,
})
const tiktok__wrapped_sync_business_centers_parse = z.object({
    message : z.string(),
    data : P.tiktok__business_centers_parse,
})
const tiktok__wrapped_sync_identities_parse = z.object({
    message : z.string(),
    data : P.tiktok__identities_parse,
})
const tiktok__wrapped_sync_pixels_parse = z.object({
    message : z.string(),
    data : P.tiktok__pixels_parse,
})

const tiktok__prefix_ttidtag = <t2, t extends t2>(xs : t[], sortfunc : (x : t2, y : t2) => number, prefix : tt_asset_tag, get : (x : t) => P.ttid) : (t & has_tagged_ttid)[] => {
    xs.sort(sortfunc)
    xs.forEach((val) => {
        const v = val as unknown as t & has_tagged_ttid
        v.tagged_id = tiktok__tag_id(prefix, get(val))
    })
    return xs as unknown as (t & has_tagged_ttid)[]
}
const sort_by_display_name = (x : { display_name : string }, y : { display_name : string }) => x.display_name.localeCompare(y.display_name)
const sort_by_pixel_name = (x : { pixel_name : string }, y : { pixel_name : string }) => x.pixel_name.localeCompare(y.pixel_name)

const tiktok__fetch_latest = async (token : authn_token, dbid : string, set_eph_state : (e : tt_edit_state) => void) => {
    const reqs = P.tiktok__requests_for_all_assets(dbid, token)
    const result = await P.all_concurrently_rec(reqs)
    const {tt_ad_accounts : a, tt_business_centers : b, tt_identities : i, tt_pixels : p} = result
    const [aok, aresp] = a
    const [bok, bresp] = b
    const [iok, iresp] = i
    const [pok, presp] = p

    if (aok && bok && iok && pok) {
        set_eph_state({
            tag : "cna-loaded",
            currently_associated : {
                tt_ad_accounts : tiktok__prefix_ttidtag(aresp, sort_by_name, "tt-aa-", tiktok__get_advertiser_id),
                tt_business_centers : tiktok__prefix_ttidtag(bresp, sort_by_name, "tt-bc-", tiktok__get_bc_id),
                tt_identities : tiktok__prefix_ttidtag(iresp, sort_by_display_name, "tt-id-", tiktok__get_identity_id),
                tt_pixels : tiktok__prefix_ttidtag(presp, sort_by_pixel_name, "tt-px-", tiktok__get_pixel_id),
            } })
    } else {
        console.log("XXXih: do error handling", result)
    }
}

const tiktok__begin_edit = async (token : authn_token, dbid : string, start_state : network_loaded<tiktok_assets>, set_eph_state : (e : tt_edit_state) => void) => {
    {
        const next_state : network_preparing_edit<tiktok_assets> = { tag : "cna-preparing-edit", currently_associated : start_state.currently_associated }
        set_eph_state(next_state)
    }
    const adact_task = issue_json_get(v1api`social/tiktok/sync/${dbid}/ad-accounts`, tiktok__wrapped_sync_ad_accounts_parse, token)
    const bctrs_task = issue_json_get(v1api`social/tiktok/sync/${dbid}/business-centers`, tiktok__wrapped_sync_business_centers_parse, token)
    const ident_task = issue_json_get(v1api`social/tiktok/sync/${dbid}/identities`, tiktok__wrapped_sync_identities_parse, token)
    const pixel_task = issue_json_post(v1api`social/tiktok/sync/${dbid}/pixels`, undefined, tiktok__wrapped_sync_pixels_parse, token)
    const result = await Promise.all([adact_task, bctrs_task, ident_task, pixel_task])
    const [a, b, i, p] = result
    const [aok, aresp] = a
    const [bok, bresp] = b
    const [iok, iresp] = i
    const [pok, presp] = p
    if (aok && bok && iok && pok) {
        const currently_used_netids = new Set<tagged_ttid>()
        const netid_to_eresid = new Map<tagged_ttid, number>()
        const add_eresid = (xs : (has_tagged_ttid & has_id)[]) => {
            for (const e of xs) netid_to_eresid.set(e.tagged_id, e.id)
        }
        const add_current = (xs : (has_tagged_ttid & has_id)[]) => {
            for (const e of xs) currently_used_netids.add(e.tagged_id)
            add_eresid(xs)
        }
        add_current(start_state.currently_associated.tt_ad_accounts)
        add_current(start_state.currently_associated.tt_business_centers)
        add_current(start_state.currently_associated.tt_identities)
        add_current(start_state.currently_associated.tt_pixels)
        const filter_prefix_nettag = <t,>(prefix : tt_asset_tag, get : (x : t) => P.ttid) => <t2 extends t>(x : t2) : result<t2 & has_tagged_ttid, undefined> => {
            const tagged_id = tiktok__tag_id(prefix, get(x))
            if (currently_used_netids.has(tagged_id)) return [Err, undefined]
            const r = x as unknown as t2 & has_tagged_ttid
            r.tagged_id = tagged_id
            return [Ok, r]
        }
        const not_currently_associated : tiktok_assets = {
            tt_ad_accounts : filter_map(aresp.data, filter_prefix_nettag("tt-aa-", tiktok__get_advertiser_id)),
            tt_business_centers : filter_map(bresp.data, filter_prefix_nettag("tt-bc-", tiktok__get_bc_id)),
            tt_identities : filter_map(iresp.data, filter_prefix_nettag("tt-id-", tiktok__get_identity_id)),
            tt_pixels : filter_map(presp.data, filter_prefix_nettag("tt-px-", tiktok__get_pixel_id)),
        }
        add_eresid(not_currently_associated.tt_ad_accounts)
        add_eresid(not_currently_associated.tt_business_centers)
        add_eresid(not_currently_associated.tt_identities)
        add_eresid(not_currently_associated.tt_pixels)
        set_eph_state({
            tag : "cna-editing",
            already_used_ids : currently_used_netids,
            selected_ids : currently_used_netids,
            id_to_eresid : netid_to_eresid,
            currently_associated : start_state.currently_associated,
            not_currently_associated : not_currently_associated,
        })
    } else {
        // XXXih: TODO: display an error message
        set_eph_state(start_state)
        console.log("begin edit failed!!!")
    }
}

const tiktok__submit_edited_data = async (token : authn_token, dbid : string, start_state : network_editing<tiktok_assets, tagged_ttid>, new_selected_id_set : Set<tagged_ttid>, set_eph_state : (e : tt_edit_state) => void) => {
    set_eph_state({
        tag : "cna-edits-submitted",
        already_used_ids : start_state.already_used_ids,
        selected_ids : new_selected_id_set,
        currently_associated : start_state.currently_associated,
        not_currently_associated : start_state.not_currently_associated,
    })
    const id_sets : Record<tt_asset_tag, { add : tagged_ttid[], remove : tagged_ttid[] }> = {
        "tt-aa-" : { add : [], remove : [] },
        "tt-bc-" : { add : [], remove : [] },
        "tt-id-" : { add : [], remove : [] },
        "tt-px-" : { add : [], remove : [] },
    }
    const filter_by_prefix = (xs : Set<tagged_ttid>, prefix : tt_asset_tag) : tagged_ttid[] => {
        const ret : tagged_ttid[] = []
        xs.forEach((val : tagged_ttid) => {
            if (val.startsWith(prefix)) {
                ret.push(val)
            }
        })
        return ret
    }
    const ids_to_add = set_difference(new_selected_id_set, start_state.already_used_ids)
    const ids_to_remove = set_difference(start_state.already_used_ids, new_selected_id_set)
    console.log(new_selected_id_set)
    console.log(ids_to_add)
    console.log(ids_to_remove)
    const extract_category = (prefix : tt_asset_tag) : void => {
        const add_ = filter_by_prefix(ids_to_add, prefix)
        const remove_ = filter_by_prefix(ids_to_remove, prefix)
        const dst = id_sets[prefix]
        dst.add = add_
        dst.remove = remove_
    }
    const _tid_to_eresid = (tagged_id : tagged_ttid, errctx : string) : number | undefined => {
        const eresid = start_state.id_to_eresid.get(tagged_id)
        if (eresid === undefined) console.log(`${errctx}: missing fbid->eresid`)
        return eresid
    }
    const __tid_to_eresid = (errctx : string) => (tagged_id : tagged_ttid) : result<number, undefined> => {
        const v = _tid_to_eresid(tagged_id, errctx)
        if (v === undefined) return [Err, undefined]
        return [Ok, v]
    }
    extract_category("tt-aa-")
    extract_category("tt-bc-")
    extract_category("tt-id-")
    extract_category("tt-px-")
    const tasks : (() => Promise<result<any, TypeError | http_error | SyntaxError | z.ZodError>>)[] = []

    const adact_body = { ids_to_sync : filter_map(id_sets["tt-aa-"].add, __tid_to_eresid("ttadacct add")) }
    if (adact_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/tiktok/sync-selected-ad-accounts/${dbid}`, adact_body, parse_any, token))
    }
    for (const tid of id_sets["tt-aa-"].remove) {
        const eresid = _tid_to_eresid(tid, "ttadacct remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/tiktok/${dbid}/ad-accounts/${eresid}`, parse_any, token))
        }
    }

    const bc_body = { ids_to_sync : filter_map(id_sets["tt-bc-"].add, __tid_to_eresid("ttbc add")) }
    if (bc_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/tiktok/sync-selected-business-centers/${dbid}`, bc_body, parse_any, token))
    }
    for (const tid of id_sets["tt-bc-"].remove) {
        const eresid = _tid_to_eresid(tid, "ttbc remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/tiktok/${dbid}/business-centers/${eresid}`, parse_any, token))
        }
    }

    const idents_body = { ids_to_sync : filter_map(id_sets["tt-id-"].add, __tid_to_eresid("ttident add")) }
    if (idents_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/tiktok/sync-selected-identities/${dbid}`, idents_body, parse_any, token))
    }
    for (const tid of id_sets["tt-id-"].remove) {
        const eresid = _tid_to_eresid(tid, "ttident remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/tiktok/${dbid}/identities/${eresid}`, parse_any, token))
        }
    }

    const pixels_body = { ids_to_sync : filter_map(id_sets["tt-px-"].add, __tid_to_eresid("ttpixel add")) }
    if (pixels_body.ids_to_sync.length > 0) {
        tasks.push(() => issue_json_post(v1api`social/tiktok/sync-selected-pixels/${dbid}`, pixels_body, parse_any, token))
    }
    for (const tid of id_sets["tt-px-"].remove) {
        const eresid = _tid_to_eresid(tid, "ttpixel remove")
        if (eresid !== undefined) {
            tasks.push(() => issue_json_delete(v1api`social/tiktok/${dbid}/pixels/${eresid}`, parse_any, token))
        }
    }

    const resp = await limited_concurrency(5, tasks)
    for (const [ok, val] of resp) {
        if (!ok) {
            console.log(`XXXih: TODO: got error: ${val}`)
        }
    }
    console.log(resp)
    await tiktok__fetch_latest(token, dbid, set_eph_state)
}

const get_display_name = (x : { display_name : string }) => x.display_name
const get_pixel_name = (x : { pixel_name : string }) => x.pixel_name
const tiktok__get_bc_id = (x : { bc_id : P.ttid }) => x.bc_id
const tiktok__get_advertiser_id = (x : { advertiser_id : P.ttid }) => x.advertiser_id
const tiktok__get_identity_id = (x : { identity_id : P.ttid }) => x.identity_id
const tiktok__get_pixel_id = (x : { pixel_id : P.ttid }) => x.pixel_id
const tiktok__business_center_url = (x : { bc_id : P.ttid }) => `https://business.tiktok.com/manage/overview?org_id=${x.bc_id}`
const tiktok__admanager_url = (x : { advertiser_id : P.ttid }) => `https://ads.tiktok.com/i18n/dashboard?aadvid=${x.advertiser_id}`

export const tiktok__netstuff : network_stuff<tiktok_assets, tagged_ttid> = {
    begin_edit : tiktok__begin_edit,
    fetch_latest : tiktok__fetch_latest,
    submit_edited_data : tiktok__submit_edited_data,
    validate_tid : tiktok__validate_tid,
    asset_table_layout : [
        ["Business Centers", "business centers", "tt_business_centers", get_name,         tiktok__get_bc_id,         tiktok__business_center_url, editable, Asset_instruction_placeholder],
        ["Ad Accounts",      "ad accounts",      "tt_ad_accounts",      get_name,         tiktok__get_advertiser_id, tiktok__admanager_url,       editable, Asset_instruction_placeholder],
        ["Identities",       "identities",       "tt_identities",       get_display_name, tiktok__get_identity_id,   XXXih_fake_url,              editable, Asset_instruction_placeholder],
        ["Pixels",           "pixels",           "tt_pixels",           get_pixel_name,   tiktok__get_pixel_id,      XXXih_fake_url,              editable, Asset_instruction_placeholder],
    ],
}

/******************************************************************************
 ** Tiktok remote data send/receive ]
 ******************************************************************************/

/******************************************************************************
 ** X (Twitter) remote data send/receive [
 ******************************************************************************/

type x_asset_tag = "x-aa-" | "x-px-"
type tagged_xid = string & { readonly __tag : unique symbol }
type has_tagged_xid = { tagged_id : tagged_xid }

const x__tag_id = (tag : x_asset_tag, xid : string) : tagged_xid => _tag_id(tag, xid)
const x__validate_tid = (maybe_tid : string) : tagged_xid | undefined => {
    const tag = maybe_tid.substring(0, 5)
    switch (tag) {
    case "x-aa-": case "x-px-": return maybe_tid as tagged_xid
    }
    return undefined
}

type x_assets = {
    ad_accounts : (T.x_ad_account_t & has_tagged_xid)[],
    pixels : (T.x_pixel_t & has_tagged_xid)[],
}

// Parsers for wrapped responses {ad_accounts: [...]} or {pixels: [...]}
const x__wrapped_ad_accounts_parse = z.object({
    ad_accounts: T.x_ad_accounts_schema
}).transform(data => data.ad_accounts)

const x__wrapped_pixels_parse = z.object({
    pixels: T.x_pixels_schema
}).transform(data => data.pixels)

// Parsers for sync endpoints that may include status
const x__wrapped_sync_ad_accounts_parse = T.x_ad_accounts_schema
const x__wrapped_sync_pixels_parse = z.object({
    status: z.string(),
    pixels: T.x_pixels_schema
}).transform(data => data.pixels)

const x__get_ad_account_x_id = (x : { x_id : string }) => x.x_id
const x__get_pixel_x_id = (x : { x_id : string }) => x.x_id

const x__prefix_xidtag = <t2, t extends t2>(xs : t[], sortfunc : (x : t2, y : t2) => number, prefix : x_asset_tag, get : (x : t) => string) : (t & has_tagged_xid)[] => {
    xs.sort(sortfunc)
    xs.forEach((val) => {
        const v = val as unknown as t & has_tagged_xid
        v.tagged_id = x__tag_id(prefix, get(val))
    })
    return xs as unknown as (t & has_tagged_xid)[]
}

const x__fetch_latest = async (token : authn_token, dbid : string, set_eph_state : (e : x_edit_state) => void) => {
    // Fetch from MAIN table (saved assets only), not INDEX (all available assets)
    const ad_accounts_task = issue_json_get(v1api`social/x/${dbid}/ad-accounts`, T.x_ad_accounts_schema, token)
    const pixels_task = issue_json_get(v1api`social/x/${dbid}/pixels`, T.x_pixels_schema, token)

    const result = await Promise.all([ad_accounts_task, pixels_task])
    const [a, p] = result
    const [aok, aresp] = a
    const [pok, presp] = p

    if (aok && pok) {
        set_eph_state({
            tag : "cna-loaded",
            currently_associated : {
                ad_accounts : x__prefix_xidtag(aresp, sort_by_name, "x-aa-", x__get_ad_account_x_id),
                pixels : x__prefix_xidtag(presp, sort_by_name, "x-px-", x__get_pixel_x_id),
            } })
    }
}

const x__begin_edit = async (token : authn_token, dbid : string, start_state : network_loaded<x_assets>, set_eph_state : (e : x_edit_state) => void) => {
    {
        const next_state : network_preparing_edit<x_assets> = { tag : "cna-preparing-edit", currently_associated : start_state.currently_associated }
        set_eph_state(next_state)
    }
    // Sync ad accounts from X API to INDEX
    const ad_accounts_sync_task = issue_json_post(v1api`social/x/${dbid}/ad-accounts/sync`, undefined, x__wrapped_sync_ad_accounts_parse, token)
    const [aaok, aaresp] = await ad_accounts_sync_task

    if (!aaok) {
        return
    }

    // Sync pixels for each ad account to INDEX
    const pixel_sync_tasks = aaresp.map(aa =>
        issue_json_post(v1api`social/x/${dbid}/ad-accounts/${aa.id}/pixels/sync`, undefined, x__wrapped_sync_pixels_parse, token)
    )
    const pixel_results = await Promise.all(pixel_sync_tasks)

    // Combine all pixels from all ad accounts
    const all_pixels : typeof aaresp extends Array<infer _> ? T.x_pixel_t[] : never = []
    for (const [pok, presp] of pixel_results) {
        if (pok) {
            all_pixels.push(...presp)
        }
    }

    const aresp = aaresp
    const presp = all_pixels

    if (true) {
        const currently_used_netids = new Set<tagged_xid>()
        const netid_to_eresid = new Map<tagged_xid, number>()
        const add_eresid = (xs : (has_tagged_xid & has_id)[]) => {
            for (const e of xs) netid_to_eresid.set(e.tagged_id, e.id)
        }
        const add_current = (xs : (has_tagged_xid & has_id)[]) => {
            for (const e of xs) currently_used_netids.add(e.tagged_id)
        }
        add_eresid(start_state.currently_associated.ad_accounts)
        add_eresid(start_state.currently_associated.pixels)
        add_current(start_state.currently_associated.ad_accounts)
        add_current(start_state.currently_associated.pixels)

        // Filter available assets: remove ones that are already associated
        const filter_map = <t,>(xs : t[], f : (x : t) => result<t & has_tagged_xid, undefined>) : (t & has_tagged_xid)[] => {
            const ret : (t & has_tagged_xid)[] = []
            for (const x of xs) {
                const [ok, r] = f(x)
                if (ok) ret.push(r)
            }
            return ret
        }
        const filter_prefix_xidtag = (prefix : x_asset_tag, get : (x : any) => string) => <t,>(x : t) : result<t & has_tagged_xid, undefined> => {
            const xid = get(x)
            const tagged_id = x__tag_id(prefix, xid)
            if (currently_used_netids.has(tagged_id)) return [Err, undefined]
            const r = x as unknown as t & has_tagged_xid
            r.tagged_id = tagged_id
            return [Ok, r]
        }

        const not_currently_associated : x_assets = {
            ad_accounts : filter_map(aresp, filter_prefix_xidtag("x-aa-", x__get_ad_account_x_id)),
            pixels : filter_map(presp, filter_prefix_xidtag("x-px-", x__get_pixel_x_id)),
        }
        add_eresid(not_currently_associated.ad_accounts)
        add_eresid(not_currently_associated.pixels)

        const next_state : network_editing<x_assets, tagged_xid> = {
            tag : "cna-editing",
            already_used_ids : currently_used_netids,
            selected_ids : currently_used_netids,
            id_to_eresid : netid_to_eresid,
            currently_associated : start_state.currently_associated,
            not_currently_associated : not_currently_associated,
        }
        set_eph_state(next_state)
    }
}

const x__submit_edited_data = async (token : authn_token, dbid : string, start_state : network_editing<x_assets, tagged_xid>, new_selected_id_set : Set<tagged_xid>, set_eph_state : (e : x_edit_state) => void) => {
    set_eph_state({
        tag : "cna-edits-submitted",
        already_used_ids : start_state.already_used_ids,
        selected_ids : new_selected_id_set,
        currently_associated : start_state.currently_associated,
        not_currently_associated : start_state.not_currently_associated,
    })

    const id_sets : Record<x_asset_tag, { add : tagged_xid[], remove : tagged_xid[] }> = {
        "x-aa-" : { add : [], remove : [] },
        "x-px-" : { add : [], remove : [] },
    }

    const filter_by_prefix = (xs : Set<tagged_xid>, prefix : x_asset_tag) : tagged_xid[] => {
        const ret : tagged_xid[] = []
        xs.forEach((val : tagged_xid) => {
            if (val.startsWith(prefix)) {
                ret.push(val)
            }
        })
        return ret
    }

    for (const prefix of Object.keys(id_sets) as x_asset_tag[]) {
        const currently_selected = filter_by_prefix(new_selected_id_set, prefix)
        const previously_selected = filter_by_prefix(start_state.already_used_ids, prefix)
        for (const cs of currently_selected) {
            if (!previously_selected.includes(cs)) {
                id_sets[prefix].add.push(cs)
            }
        }
        for (const ps of previously_selected) {
            if (!currently_selected.includes(ps)) {
                id_sets[prefix].remove.push(ps)
            }
        }
    }

    const _tid_to_eresid = (tid : tagged_xid, ctx : string) : number | undefined => {
        const eresid = start_state.id_to_eresid.get(tid)
        if (eresid === undefined) console.log(`XXXih: missing eres id for tid ${tid} in context ${ctx}`)
        return eresid
    }
    const __tid_to_eresid = (ctx : string) => (tid : tagged_xid) : result<number, undefined> => {
        const eresid = _tid_to_eresid(tid, ctx)
        return eresid === undefined ? [Err, undefined] : [Ok, eresid]
    }

    // IMPORTANT: Ad accounts must be saved BEFORE pixels (backend requirement)
    const ad_account_tasks : (() => Promise<result<any, TypeError | http_error | SyntaxError | z.ZodError>>)[] = []
    const pixel_tasks : (() => Promise<result<any, TypeError | http_error | SyntaxError | z.ZodError>>)[] = []

    // Ad Accounts - send X IDs as objects [{x_id: "..."}, ...]
    if (id_sets["x-aa-"].add.length > 0) {
        const to_sync = id_sets["x-aa-"].add.map(tid => ({ x_id: tid.slice(5) }))
        ad_account_tasks.push(() => issue_json_post(v1api`social/x/${dbid}/ad-accounts/sync-selected-ad-accounts`, to_sync, parse_any, token))
    }
    // Ad Accounts - remove using MAIN eres IDs
    for (const tid of id_sets["x-aa-"].remove) {
        const eresid = _tid_to_eresid(tid, "x adacct remove")
        if (eresid !== undefined) {
            ad_account_tasks.push(() => issue_json_delete(v1api`social/x/${dbid}/ad-accounts/${eresid}`, parse_any, token))
        }
    }

    // Execute ad account operations first and wait for completion
    const ad_resp = await limited_concurrency(5, ad_account_tasks)
    for (const [ok, val] of ad_resp) {
        if (!ok) {
            console.log(`XXXih: ad account error: ${val}`)
        }
    }

    // Now execute pixel operations after ad accounts are saved
    // Pixels - send X IDs as objects [{x_id: "..."}, ...]
    if (id_sets["x-px-"].add.length > 0) {
        const to_sync = id_sets["x-px-"].add.map(tid => ({ x_id: tid.slice(5) }))
        pixel_tasks.push(() => issue_json_post(v1api`social/x/${dbid}/pixels/sync-selected-pixels`, to_sync, parse_any, token))
    }
    // Pixels - remove using MAIN eres IDs
    for (const tid of id_sets["x-px-"].remove) {
        const eresid = _tid_to_eresid(tid, "x pixel remove")
        if (eresid !== undefined) {
            pixel_tasks.push(() => issue_json_delete(v1api`social/x/${dbid}/pixels/${eresid}`, parse_any, token))
        }
    }

    const resp = await limited_concurrency(5, pixel_tasks)
    for (const [ok, val] of resp) {
        if (!ok) {
            console.log(`XXXih: TODO: got error: ${val}`)
        }
    }

    await x__fetch_latest(token, dbid, set_eph_state)
}

const x__delete_asset = async (token : authn_token, dbid : string, field_name : keyof x_assets, eres_id : number, set_eph_state : (e : x_edit_state) => void) => {
    let endpoint : string
    if (field_name === "ad_accounts") {
        endpoint = v1api`social/x/${dbid}/ad-accounts/${eres_id}`
    } else if (field_name === "pixels") {
        endpoint = v1api`social/x/${dbid}/pixels/${eres_id}`
    } else {
        return
    }

    const [ok, _] = await issue_json_delete(endpoint, z.any(), token)

    if (ok) {
        await x__fetch_latest(token, dbid, set_eph_state)
    }
}

type x_edit_state = edit_state<x_assets, tagged_xid>

export const x__netstuff : network_stuff<x_assets, tagged_xid> = {
    begin_edit : x__begin_edit,
    fetch_latest : x__fetch_latest,
    submit_edited_data : x__submit_edited_data,
    validate_tid : x__validate_tid,
    asset_table_layout : [
        ["Ad Accounts", "ad accounts", "ad_accounts", get_name, x__get_ad_account_x_id, XXXih_fake_url, editable, Asset_instruction_placeholder],
        ["Pixels (Web Event Tags)", "pixels", "pixels", get_name, x__get_pixel_x_id, XXXih_fake_url, editable, Asset_instruction_placeholder],
    ],
    delete_asset : x__delete_asset,
}

/******************************************************************************
 ** X (Twitter) remote data send/receive ]
 ******************************************************************************/

type emit_row_props = {
    label : string,
    external_link : string | null,
    tagged_id : string,
    network_id : string,
    selected : boolean,
    editing : boolean,
    should_fadein : boolean,
    editing_disabled? : boolean,
    show_inline_delete_button : boolean,
    on_delete? : () => void,
}
const Emit_row = R.memo((p : emit_row_props) => {
    const [is_selected, set_selected] = R.useState(p.selected)
    const click_handler = R.useCallback((e : R.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        set_selected(e.target.checked)
    }, [])
    const delete_handler = R.useCallback((e : R.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (p.on_delete) {
            p.on_delete()
        }
    }, [p.on_delete])
    let text_color = "tw-text-big-stone-950"
    let bgcolor = ""
    let Checkbox : R.ReactElement | null = null
    if (p.editing) {
        text_color = is_selected ? "tw-text-big-stone-950" : "tw-text-gray-500"
        bgcolor = is_selected ? "" : ""
        Checkbox = <Eres_checkbox id={p.tagged_id} className="tw-me-1.5 tw-align-middle" defaultChecked={p.selected} onChange={click_handler} disabled={p.editing_disabled}/>
    }
    const style = p.should_fadein ? {animation : "eres-fadein 400ms"} : {}
    const truncate_string = (str: string, maxLength: number = 25) : string => {
        if (str.length <= maxLength) {
            return str;
          } else {
            return str.slice(0, maxLength) + "...";
        }
    }

    return (
    <tr key={p.network_id} className={`tw-border-y tw-border-x-0 tw-border-gray-200 last:tw-border-b-0 hover:tw-bg-gray-50 ${bgcolor}`} style={style}>
    <Tablecol>
        <div className="tw-inline-block tw-whitespace-nowrap">
        {Checkbox}
        <span className={`tw-text-base ${text_color} tw-font-semibold tw-align-middle`}>{truncate_string(p.label)}</span>
        </div>
    </Tablecol>
    <Tablecol>{p.network_id}</Tablecol>
    <Tablecol align="right">
        {p.show_inline_delete_button ? <a href="#" onClick={delete_handler}><FontAwesomeIcon icon={faTrash} aria-hidden="true"/></a> : null}
        {p.external_link !== null
            ? (<a href={p.external_link} target="_blank"><FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" className="tw-ps-1.5"/></a>)
            : ""}
    </Tablecol>
    </tr>)
})

const _emit_row = <id_t extends string, t1, t2, t3>(
    get_label : (x : t1) => string,
    external_link : ((x : t2) => string) | null,
    get_network_id : (x : t3) => string,
    selected : Set<id_t> | boolean,
    editing : boolean,
    should_fadein : boolean,
    editing_disabled : boolean,
    show_inline_delete_button : boolean,
    on_delete? : (eres_id : number) => void,
) => (x : t1 & t2 & t3 & has_id & has_tagged_id<id_t>) => {
    const network_id = get_network_id(x)
    let is_selected = false
    switch (true) {
    case selected instanceof Set: is_selected = selected.has(x.tagged_id); break
    case typeof(selected) === "boolean": is_selected = selected; break
    default: unreachable(selected)
    }
    const delete_handler = on_delete ? () => on_delete(x.id) : undefined
    return <Emit_row key={network_id} label={get_label(x)} tagged_id={x.tagged_id} network_id={network_id} external_link={external_link ? external_link(x) : null} selected={is_selected} editing={editing} should_fadein={should_fadein} editing_disabled={editing_disabled} show_inline_delete_button={show_inline_delete_button} on_delete={delete_handler}/>
}

export const Asset_table = <assets_t extends fields_are_arrays, id_t extends string>({eph_state, netstuff, token, dbid, set_eph_state} : {eph_state : edit_state<assets_t, id_t>, netstuff : network_stuff<assets_t, id_t>, token? : authn_token, dbid? : string, set_eph_state? : (e : edit_state<assets_t, id_t>) => void}) => {
    let is_editing : boolean = false
    let no_editing : boolean = false
    switch (eph_state.tag) {
    case "cna-edits-submitted": is_editing = true; no_editing = true; break
    case "cna-editing": is_editing = true; break
    case "cna-loaded": is_editing = false; break
    case "cna-preparing-edit": is_editing = false; break
    default: unreachable(eph_state)
    }
    const show_inline_delete_button = !is_editing
    const emit_group = (layout_item : asset_layout<assets_t>) => {
        const [title, lowercase_title, field, get_name, get_network_id, get_url, is_editable, Instruction_component] = layout_item
        const xs : any[] = eph_state.currently_associated[field]
        const editing_disabled = is_editable === not_editable || no_editing
        let count = xs.length
        let tail : R.ReactElement[] | null = null
        let selected : Set<id_t> | boolean = true

        // Create delete handler if delete_asset function is provided
        const delete_handler = (netstuff.delete_asset && token && dbid && set_eph_state)
            ? (eres_id : number) => {
                void netstuff.delete_asset!(token, dbid, field, eres_id, set_eph_state)
            }
            : undefined

        if (eph_state.tag === "cna-editing" || eph_state.tag === "cna-edits-submitted") {
            const nca = eph_state.not_currently_associated[field]
            count = count + nca.length
            selected = eph_state.selected_ids
            tail = nca.map(_emit_row(get_name, get_url, get_network_id, selected, true, true, editing_disabled, show_inline_delete_button, undefined))
        }
        const body = count === 0
            ? <tr>
                <td colSpan={3} className="tw-text-sm tw-text-gray-400 tw-ps-9">
                    No connected {lowercase_title}.
                    <Eres_collapse title="See instructions">
                        <Instruction_component />
                    </Eres_collapse>
                    <Eres_iconbutton className="tw-mt-2" id="f-sync" icon={faSync} type="submit">Sync</Eres_iconbutton>
                </td>
                </tr>
            : (<>
                {xs.map(_emit_row(get_name, get_url, get_network_id, selected, is_editing, false, editing_disabled, show_inline_delete_button, delete_handler))}
                {tail}
            </>)
        return (<R.Fragment key={title}>
            <tr><th colSpan={3} className="tw-text-left">
                <Eres_headerxl className="tw-pt-6 tw-px-6 tw-mb-3">{title}</Eres_headerxl>
            </th></tr>
            {body}
            </R.Fragment>)
    }
    return (<>
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-min-w-[600px] tw-mb-3 tw-table-auto">
                <colgroup>
                    <col span={1} style={{width : "60%"}}/>
                    <col span={1} style={{width : "30%"}}/>
                    <col span={1} style={{width : "10%"}}/>
                </colgroup>
                <tbody>
                    {netstuff.asset_table_layout.map(emit_group)}
                </tbody>
            </table>
        </div>
        </>
    )
}

const Wrap = ({buttons, children, submit_handler, reset_handler} : R.PropsWithChildren<
    { buttons : R.ReactElement | null
    , submit_handler? : R.FormEventHandler<HTMLFormElement>
    , reset_handler? : R.FormEventHandler<HTMLFormElement>
    }>
) => {
    return (
    <Eres_card>
        <form onSubmit={submit_handler} onReset={reset_handler}>
        <fieldset>
        <div className="tw-mt-6 tw-px-6 tw-mb-3 tw-flex tw-flex-col sm:tw-flex-row tw-gap-3">
            <div className="tw-flex-grow">
                <Eres_header2xl>Connected Assets</Eres_header2xl>
            </div>
            <div className="tw-flex tw-flex-grow-0">
                {buttons}
            </div>
        </div>
        {children}
        </fieldset>
        </form>
    </Eres_card>)
}

const parse_prefixed_dbid = (prefixed_dbid : string) : result<tagged_network_id, undefined> => {
    let tag : "fb" | "li" | "tt" | "x"
    let id : string
    if (prefixed_dbid.startsWith("fb-")) {
        tag = "fb"
        id = prefixed_dbid.slice(3)
    } else if (prefixed_dbid.startsWith("li-")) {
        tag = "li"
        id = prefixed_dbid.slice(3)
    } else if (prefixed_dbid.startsWith("tt-")) {
        tag = "tt"
        id = prefixed_dbid.slice(3)
    } else if (prefixed_dbid.startsWith("x-")) {
        tag = "x"
        id = prefixed_dbid.slice(2)
    } else {
        return [Err, undefined]
    }
    return [Ok, [tag, id as network_dbid]]
}

const Connected_network_details = <assets_t extends fields_are_arrays, id_t extends string>(
    props : { program_state : program_state, user_state : user_state, net_dbid : tagged_network_id, netstuff : network_stuff<assets_t, id_t> },
) => {
    const user_state = props.user_state
    const net_dbid = props.net_dbid
    const [, raw_net_dbid] = net_dbid
    const netstuff = props.netstuff
    const [eph_state, set_eph_state] = R.useState({ tag : "loading" } as pending_edit_state<assets_t, id_t>)
    run_once_on_component_creation(() => {
        void netstuff.fetch_latest(user_state.token, raw_net_dbid, set_eph_state)
    })
    let disabled = true
    let children : R.ReactElement | null = null
    const emit_deets = (es : edit_state<assets_t, id_t>) => {
        children = <Asset_table eph_state={es} netstuff={netstuff} token={user_state.token} dbid={raw_net_dbid} set_eph_state={set_eph_state}/>
    }
    const begin_edit = (st : network_loaded<assets_t>) => (e : R.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        void netstuff.begin_edit(user_state.token, raw_net_dbid, st, set_eph_state)
    }
    let button_strip : R.ReactElement | null = null
    const use_edit_buttons = (editor_loading : boolean, edit_action : (e : R.MouseEvent<HTMLButtonElement>) => void) => {
        button_strip = <Eres_iconbutton icon={editor_loading ? "eres-spinner" : faPencil} onClick={edit_action} disabled={disabled} type="button">Add/Remove Assets</Eres_iconbutton>
    }
    let form_submit_handler : R.FormEventHandler<HTMLFormElement> | undefined
    let form_reset_handler : R.FormEventHandler<HTMLFormElement> | undefined
    const use_cancel_save_buttons = (st : network_editing<assets_t, id_t> | network_edits_submitted<assets_t, id_t>) => {
        form_submit_handler = (e : R.FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            switch (st.tag) {
            case "cna-edits-submitted": return
            case "cna-editing": break
            default: unreachable(st)
            }
            const ne = e.nativeEvent
            if (!(ne instanceof SubmitEvent)) return
            if (ne.submitter === null) return
            if (ne.submitter.id !== "f-save") return
            const ct = e.currentTarget
            const len = ct.length
            const selected_facebook_ids = new Set<id_t>()
            for (let i = 0; i < len; ++i) {
                const elt = ct[i]
                if (!(elt instanceof HTMLInputElement)) continue
                if (elt.type !== "checkbox") continue
                if (elt.checked) {
                    const eid = elt.id
                    const efbid = netstuff.validate_tid(eid)
                    if (efbid) selected_facebook_ids.add(efbid)
                }
            }
            void netstuff.submit_edited_data(user_state.token, raw_net_dbid, st, selected_facebook_ids, set_eph_state)
        }
        form_reset_handler = (e : R.FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            switch (st.tag) {
            case "cna-edits-submitted": return
            case "cna-editing": break
            default: unreachable(st)
            }
            set_eph_state({
                tag : "cna-loaded",
                currently_associated : st.currently_associated })
        }
        button_strip = <>
            <Eres_iconbutton id="f-cancel" icon={faBan} type="reset" disabled={disabled} className="tw-me-1.5">Cancel</Eres_iconbutton>
            <Eres_iconbutton id="f-save" icon={faFloppyDisk} type="submit" disabled={disabled}>Save</Eres_iconbutton>
        </>
    }
    switch (eph_state.tag) {
    case "loading": disabled = true; use_edit_buttons(false, stop_propagation); children = <Eres_spin_placeholder className="tw-p-6"/>; break
    case "cna-preparing-edit": disabled = true; use_edit_buttons(true, stop_propagation); emit_deets(eph_state); break
    case "cna-editing": disabled = false; use_cancel_save_buttons(eph_state); emit_deets(eph_state); break
    case "cna-loaded": disabled = false; use_edit_buttons(false, begin_edit(eph_state)); emit_deets(eph_state); break
    case "cna-edits-submitted": disabled = true; use_cancel_save_buttons(eph_state); emit_deets(eph_state); break
    default: unreachable(eph_state)
    }
    return <Wrap buttons={button_strip} submit_handler={form_submit_handler} reset_handler={form_reset_handler}>{children}</Wrap>
}

type ecnd_t = { prefixed_dbid : string, program_state : program_state, user_state : user_state }
const Parse_id_and_display_network_details = ({prefixed_dbid, program_state, user_state} : ecnd_t) => {
    const [id_ok, id] = parse_prefixed_dbid(prefixed_dbid)
    if (!id_ok) return null // XXXih: TODO: figure out display of errors to the user
    const [tag, ] = id
    switch (tag) {
    case "fb": return <Connected_network_details net_dbid={id} program_state={program_state} user_state={user_state} netstuff={facebook__netstuff}/>
    case "li": return <Connected_network_details net_dbid={id} program_state={program_state} user_state={user_state} netstuff={linkedin__netstuff}/>
    case "tt": return <Connected_network_details net_dbid={id} program_state={program_state} user_state={user_state} netstuff={tiktok__netstuff}/>
    case "x": return <Connected_network_details net_dbid={id} program_state={program_state} user_state={user_state} netstuff={x__netstuff}/>
    default: unreachable(tag)
    }
}

export const Memo_view = R.memo(Parse_id_and_display_network_details)

export default function Connected_network_editor() {
    // const router = useRouter()
    // const prefixed_dbid = router.query.eres_connected_network_dbid
    const {prefixed_dbid} = useParams()
    const [ps, cs] = use_authenticated_program_state()
    let page : R.ReactElement
    if (prefixed_dbid === undefined) page = <h1>917559be435543f174a0f3d1193f75f8 missing dbid</h1>
    else page = <Memo_view prefixed_dbid={prefixed_dbid} program_state={ps} user_state={cs}/>
    return (<Emit_main page_id="PAGE-connected-networks">{page}</Emit_main>)
}
